import { Shield } from 'lucide-react';
import { Card, CardHeader, CardContent, VStack, HStack } from '@components/base/layout';
import { Text, Icon, Badge } from '@components/base/display';
import type { AccessibilityInfo } from '../../showcases/types';

interface AccessibilityPanelProps {
  accessibility: AccessibilityInfo;
}

export function AccessibilityPanel({ accessibility }: Readonly<AccessibilityPanelProps>) {
  const hasContent =
    accessibility.ariaAttributes.length > 0 ||
    accessibility.keyboardNav.length > 0 ||
    accessibility.notes.length > 0;

  if (!hasContent) {
    return null;
  }

  const sectionCount = [
    accessibility.ariaAttributes.length > 0,
    accessibility.keyboardNav.length > 0,
    accessibility.notes.length > 0,
  ].filter(Boolean).length;

  return (
    <Card variant="outlined">
      <CardHeader
        title="Accessibility"
        subtitle={`${sectionCount} ${sectionCount === 1 ? 'section' : 'sections'} — ARIA, keyboard navigation & notes`}
        collapsible
        defaultExpanded={false}
        action={<Icon icon={Shield} size="sm" color="muted" />}
      />
      <CardContent>
        <VStack gap={5}>
          {accessibility.ariaAttributes.length > 0 && (
            <VStack gap={2}>
              <HStack gap={2} align="center">
                <Text as="h4" size="sm" weight="semibold">
                  ARIA Attributes
                </Text>
                <Badge size="sm" variant="info" pill>
                  {accessibility.ariaAttributes.length}
                </Badge>
              </HStack>
              <VStack gap={1} className="pl-1">
                {accessibility.ariaAttributes.map((attr) => (
                  <HStack key={attr} gap={2} align="start">
                    <Text as="span" size="xs" color="muted" className="mt-1 select-none">
                      &bull;
                    </Text>
                    <Text as="span" size="sm" color="secondary">
                      {attr}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </VStack>
          )}
          {accessibility.keyboardNav.length > 0 && (
            <VStack gap={2}>
              <HStack gap={2} align="center">
                <Text as="h4" size="sm" weight="semibold">
                  Keyboard Navigation
                </Text>
                <Badge size="sm" variant="info" pill>
                  {accessibility.keyboardNav.length}
                </Badge>
              </HStack>
              <VStack gap={1} className="pl-1">
                {accessibility.keyboardNav.map((nav) => (
                  <HStack key={nav} gap={2} align="start">
                    <Text as="span" size="xs" color="muted" className="mt-1 select-none">
                      &bull;
                    </Text>
                    <Text as="span" size="sm" color="secondary">
                      {nav}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </VStack>
          )}
          {accessibility.notes.length > 0 && (
            <VStack gap={2}>
              <HStack gap={2} align="center">
                <Text as="h4" size="sm" weight="semibold">
                  Notes
                </Text>
                <Badge size="sm" variant="default" pill>
                  {accessibility.notes.length}
                </Badge>
              </HStack>
              <VStack gap={1} className="pl-1">
                {accessibility.notes.map((note) => (
                  <HStack key={note} gap={2} align="start">
                    <Text as="span" size="xs" color="muted" className="mt-1 select-none">
                      &bull;
                    </Text>
                    <Text as="span" size="sm" color="secondary">
                      {note}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </VStack>
          )}
        </VStack>
      </CardContent>
    </Card>
  );
}
